import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { GrowthFactor, HeaderTooltip } from '../projectionTable/HeaderTooltip';
import { currency } from '../projectionTable/formatters';
import {
  AccumulationWhenCell,
  GrowthValueCell,
  ScheduleGrowthTooltip,
  buildInflationTooltip,
} from './cells';
import { AccumulationColumnSpec, AccumulationCtx } from './types';

const isAllZero = (timeline: AccumulationYear[], pick: (s: YearSnapshot) => number): boolean =>
  timeline.every(r => pick(r.snapshot) <= 0.5);

function inflationIncrements(policyYear: number, capYears: number): number {
  const rawIncrements = Math.max(Math.floor(policyYear) - 1, 0);
  return capYears > 0 ? Math.min(rawIncrements, capYears) : rawIncrements;
}

function inflationFactor({
  rate,
  capYears,
  isSimple,
  policyYear,
}: {
  rate: number;
  capYears: number;
  isSimple: boolean;
  policyYear: number;
}): number {
  const increments = inflationIncrements(policyYear, capYears);
  return isSimple ? 1 + rate * increments : Math.pow(1 + rate, increments);
}

function formatPercent(rate: number): string {
  const pct = rate * 100;
  return `${pct.toFixed(pct === Math.round(pct) ? 0 : 2)}%`;
}

function poolFormula(
  rate: number,
  capYears: number,
  isSimple: boolean,
  startingLabel = 'at-issue LTC pool',
) {
  return (
    <>
      {startingLabel} x{' '}
      <GrowthFactor
        rate={formatPercent(rate)}
        exponent={
          capYears > 0 ? `min(completed policy years, ${capYears})` : 'completed policy years'
        }
        simple={isSimple}
      />
    </>
  );
}

function buildSplitPoolGrowthTooltip({ terms, row }: { terms: ClaimTerms; row: AccumulationYear }) {
  const baseAtIssue = terms.baseBenefitPoolAtIssue ?? 0;
  const extensionAtIssue = terms.extensionOfBenefitsPoolAtIssue ?? 0;
  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const extensionRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const extensionCapYears =
    terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const isSimple = terms.inflationIsSimple === true;
  const baseValue =
    baseAtIssue *
    inflationFactor({
      rate: baseRate,
      capYears: baseCapYears,
      isSimple,
      policyYear: row.policyYear,
    });
  const extensionValue =
    extensionAtIssue *
    inflationFactor({
      rate: extensionRate,
      capYears: extensionCapYears,
      isSimple,
      policyYear: row.policyYear,
    });

  return (
    <ScheduleGrowthTooltip
      startingLabel={`At issue (${terms.policyPremiumStartYear})`}
      startingValue={baseAtIssue + extensionAtIssue}
      result={row.snapshot.theoreticalLtcPoolCeiling}
      driverLabel="Base pool + extension pool"
      rows={[
        ['Base pool', baseValue],
        ['Extension pool', extensionValue],
      ]}
    />
  );
}

// Universal accumulation columns, derived from `terms` + each year's snapshot.
// Strategies override via `StrategyPresentation.accumulationColumns` when a
// product needs a different headline balance (e.g. an annuity's contract value)
// — see the per-strategy presentation files.
export function buildDefaultAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  const atIssuePool = timeline[0]?.snapshot.theoreticalLtcPoolCeiling ?? 0;
  const poolRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const poolCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const extensionPoolRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const extensionPoolCapYears =
    terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const hasExtensionPool =
    (terms.extensionOfBenefitsPoolAtIssue ?? 0) > 0 ||
    timeline.some(row => row.snapshot.effectiveContinuationBenefit > 0.5);
  const hasSplitPoolGrowth =
    hasExtensionPool && (poolRate !== extensionPoolRate || poolCapYears !== extensionPoolCapYears);
  if (!terms.isUnlimited && !isAllZero(timeline, s => s.theoreticalLtcPoolCeiling)) {
    columns.push({
      key: 'ltc-pool',
      label: 'LTC pool',
      tooltip: (
        <HeaderTooltip
          description={
            hasSplitPoolGrowth
              ? 'The total LTC dollars shown for each policy year before care begins. The displayed value is the base pool plus the extension pool for that policy year.'
              : 'The LTC dollar pool shown for each policy year before care begins. It starts with the entered at-issue pool and grows on policy anniversaries when benefit inflation applies.'
          }
          formulas={
            hasSplitPoolGrowth
              ? [
                  { label: 'Output', expression: 'base pool + extension pool' },
                  {
                    label: 'Base pool',
                    expression: poolFormula(
                      poolRate,
                      poolCapYears,
                      terms.inflationIsSimple === true,
                      'at-issue base pool',
                    ),
                  },
                  {
                    label: 'Extension',
                    expression: poolFormula(
                      extensionPoolRate,
                      extensionPoolCapYears,
                      terms.inflationIsSimple === true,
                      'at-issue extension pool',
                    ),
                  },
                ]
              : [
                  { label: 'At issue', expression: currency(atIssuePool) },
                  {
                    label: 'Output',
                    expression: poolFormula(
                      poolRate,
                      poolCapYears,
                      terms.inflationIsSimple === true,
                    ),
                  },
                ]
          }
        />
      ),
      render: (row, prev) => {
        const value = row.snapshot.theoreticalLtcPoolCeiling;
        // Closed-form when a single rate explains the growth; otherwise (no
        // single rate, or a multi-pool ceiling that doesn't reconcile to one
        // factor) still show a tooltip via a generic at-issue → this-year block
        // so a growing pool is never left without an explanation.
        const grew = value - atIssuePool > 0.5;
        const tooltip = hasSplitPoolGrowth ? (
          buildSplitPoolGrowthTooltip({ terms, row })
        ) : poolRate > 0 ? (
          buildInflationTooltip({
            startingLabel: `At issue (${terms.policyPremiumStartYear})`,
            startingValue: atIssuePool,
            rate: poolRate,
            capYears: poolCapYears,
            isSimple: terms.inflationIsSimple === true,
            policyYear: row.policyYear,
            result: value,
            startYear: terms.policyPremiumStartYear,
          })
        ) : grew ? (
          <ScheduleGrowthTooltip
            startingLabel={`At issue (${terms.policyPremiumStartYear})`}
            startingValue={atIssuePool}
            result={value}
            driverLabel="Anniversary inflation"
          />
        ) : undefined;
        return (
          <GrowthValueCell
            value={value}
            prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
            tooltip={tooltip}
          />
        );
      },
    });
  }

  if (!isAllZero(timeline, s => s.monthlyBenefitCap)) {
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="The monthly benefit cap shown for each policy year before care begins. It starts with the projection's at-issue monthly cap and grows on policy anniversaries when benefit inflation applies."
          formulas={[
            {
              label: 'At issue',
              expression: `${currency(terms.nominalDailyBenefitBase)}/day x ${DAYS_IN_MONTH} days = ${currency(baseMonthly)}`,
            },
            {
              label: 'Output',
              expression: (
                <>
                  at-issue monthly benefit x{' '}
                  <GrowthFactor
                    rate={formatPercent(terms.inflationRate)}
                    exponent={
                      terms.inflationCapYears > 0
                        ? `min(completed policy years, ${terms.inflationCapYears})`
                        : 'completed policy years'
                    }
                    simple={terms.inflationIsSimple === true}
                  />
                </>
              ),
            },
          ]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={
            terms.inflationRate > 0
              ? buildInflationTooltip({
                  startingLabel: `At issue (${terms.policyPremiumStartYear})`,
                  startingValue: baseMonthly,
                  rate: terms.inflationRate,
                  capYears: terms.inflationCapYears,
                  isSimple: terms.inflationIsSimple === true,
                  policyYear: row.policyYear,
                  result: row.snapshot.monthlyBenefitCap,
                  startYear: terms.policyPremiumStartYear,
                })
              : undefined
          }
        />
      ),
    });
  }

  if (!isAllZero(timeline, s => s.effectiveFaceAmount)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip description="The death benefit / face amount payable if the policyholder passes before using long-term-care benefits." />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
        />
      ),
    });
  }

  return columns;
}
